import {
  BundleDownloadStatusType,
  TemplateType,
  useBundleRequestDownloadMutation,
  useGetTemplateListQuery,
  useLazyBundleDownloadStatusQuery,
  useLazyGetBundleGetDownloadQuery,
  useLazyGetBundleGetDownloadIndexQuery,
  useLazyGetChronologyGetDownloadQuery,
  useLazyGetTimelineGetDownloadQuery,
} from '@/api';
import {
  BundleType,
  selectBundleDownloadStatusByStoreKey,
  selectCaseIdData,
  selectCaseOverview,
  useAppSelector,
  useAppDispatch,
  setBundleDownloadStatus,
} from '@/store';
import { MouseEvent, useCallback, useEffect, useState, useRef } from 'react';
import { downloadFilesAsZip } from '../utils';
import { format } from 'date-fns';
import { useAzureFileUpload } from '@repo/file-uploader';
import { useGlobalToast } from '@repo/atomic-components';
import { useAccessControl } from '@/hooks';
import { AccessControlAction } from '@/constants';

const BUNDLE_DOWNLOAD_POLL_INTERVAL_MS = 20 * 1000; // 20 seconds

export const useAiProcessedDownloads = () => {
  const { downloadFile } = useAzureFileUpload();
  const dispatch = useAppDispatch();
  const globalToastRef = useGlobalToast();

  const caseId = useAppSelector(selectCaseIdData);
  const caseOverview = useAppSelector(selectCaseOverview);
  const [triggerBundleRequestDownload] = useBundleRequestDownloadMutation();
  const [triggerFullBundleDownloadStatus] = useLazyBundleDownloadStatusQuery();
  const [triggerLightBundleDownloadStatus] = useLazyBundleDownloadStatusQuery();
  const [triggerBundleGetDownload] = useLazyGetBundleGetDownloadQuery();
  const [triggerBundleGetDownloadIndex] = useLazyGetBundleGetDownloadIndexQuery();
  const [triggerTimelineGetDownload] = useLazyGetTimelineGetDownloadQuery();
  const [triggerChronologyGetDownload] = useLazyGetChronologyGetDownloadQuery();

  // Custom templates for the current org — surfaced as extra options beneath
  // the "Default" entry in the chronology / index download dropdowns. Only
  // fetched for users who can export bundles/timelines/chronologies, since the
  // menus they feed are gated behind the same permission.
  const isExportBundlesTimelinesChronologies = useAccessControl(
    AccessControlAction.EXPORT_BUNDLES_TIMELINES_CHRONOLOGIES,
  );
  const { data: chronologyTemplatesData } = useGetTemplateListQuery(
    { templateType: TemplateType.CHRONOLOGY },
    { skip: !isExportBundlesTimelinesChronologies },
  );
  const { data: indexTemplatesData } = useGetTemplateListQuery(
    { templateType: TemplateType.INDEX },
    { skip: !isExportBundlesTimelinesChronologies },
  );
  // A record is created before its blob upload is confirmed. Pending records
  // stay visible in the admin dashboard, but cannot be used to generate a
  // chronology or index yet.
  const chronologyTemplates = chronologyTemplatesData?.templates.filter((template) => template.uploadedAt) ?? [];
  const indexTemplates = indexTemplatesData?.templates.filter((template) => template.uploadedAt) ?? [];

  const fullBundleDownloadStatus = useAppSelector(selectBundleDownloadStatusByStoreKey(BundleType.FULL));
  const lightBundleDownloadStatus = useAppSelector(selectBundleDownloadStatusByStoreKey(BundleType.LIGHT));

  const [fullBundleDownloadProgress, setFullBundleDownloadProgress] = useState<number | null>(null);
  const [lightBundleDownloadProgress, setLightBundleDownloadProgress] = useState<number | null>(null);

  // Simple local state for UI - these control the button display
  const [isGeneratingFull, setIsGeneratingFull] = useState(false);
  const [isGeneratingLight, setIsGeneratingLight] = useState(false);
  const [fullBundleRequestError, setFullBundleRequestError] = useState<string | null>(null);
  const [lightBundleRequestError, setLightBundleRequestError] = useState<string | null>(null);
  const [fullIndexDownloadError, setFullIndexDownloadError] = useState<string | null>(null);
  const [lightIndexDownloadError, setLightIndexDownloadError] = useState<string | null>(null);

  // Track generation status based on Redux download status changes
  useEffect(() => {
    if (fullBundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_READY) {
      setIsGeneratingFull(false);
    } else if (fullBundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_PREPARING) {
      setIsGeneratingFull(true);
    } else if (fullBundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_PREPARATION_FAILED) {
      setIsGeneratingFull(false);
    }
  }, [fullBundleDownloadStatus]);

  useEffect(() => {
    if (lightBundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_READY) {
      setIsGeneratingLight(false);
    } else if (lightBundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_PREPARING) {
      setIsGeneratingLight(true);
    } else if (lightBundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_PREPARATION_FAILED) {
      setIsGeneratingLight(false);
    }
  }, [lightBundleDownloadStatus]);

  // On-mount: make a SINGLE call to check bundle status so the correct UI is shown
  // immediately. Only start continuous polling if status is DOWNLOAD_PREPARING.
  useEffect(() => {
    const bundleId = caseOverview?.fullBundle?.bundleId;
    if (!bundleId) return;

    triggerFullBundleDownloadStatus({ bundleId, bundleKey: BundleType.FULL }).then((response) => {
      const status = response.data?.status;
      if (status === BundleDownloadStatusType.DOWNLOAD_PREPARING) {
        // Show spinner and begin polling (delayed — the initial call already checked once)
        setIsGeneratingFull(true);
        fullPollingActiveRef.current = true;
        fullPollingTimeoutRef.current = setTimeout(
          () => startFullBundlePolling(bundleId),
          BUNDLE_DOWNLOAD_POLL_INTERVAL_MS,
        );
      } else if (status === BundleDownloadStatusType.DOWNLOAD_PREPARATION_FAILED) {
        // On refresh, show Generate button instead of error — user can retry
        dispatch(
          setBundleDownloadStatus({
            key: BundleType.FULL,
            data: BundleDownloadStatusType.IN_PROGRESS,
          }),
        );
      }
      // IN_PROGRESS → show Generate button (default), no polling
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseOverview?.fullBundle?.bundleId]);

  // On-mount: same single-call check for light bundle.
  useEffect(() => {
    const bundleId = caseOverview?.lightBundle?.bundleId;
    if (!bundleId) return;

    triggerLightBundleDownloadStatus({ bundleId, bundleKey: BundleType.LIGHT }).then((response) => {
      const status = response.data?.status;
      if (status === BundleDownloadStatusType.DOWNLOAD_PREPARING) {
        setIsGeneratingLight(true);
        lightPollingActiveRef.current = true;
        lightPollingTimeoutRef.current = setTimeout(
          () => startLightBundlePolling(bundleId),
          BUNDLE_DOWNLOAD_POLL_INTERVAL_MS,
        );
      } else if (status === BundleDownloadStatusType.DOWNLOAD_PREPARATION_FAILED) {
        // On refresh, show Generate button instead of error — user can retry
        dispatch(
          setBundleDownloadStatus({
            key: BundleType.LIGHT,
            data: BundleDownloadStatusType.IN_PROGRESS,
          }),
        );
      }
      // IN_PROGRESS → show Generate button (default), no polling
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseOverview?.lightBundle?.bundleId]);

  // Timeout handles for scheduled poll (ReturnType<typeof setTimeout> is number in browser, object in Node)
  const fullPollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lightPollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Separate refs to indicate polling is active (avoids overloading the timeout ref with a sentinel)
  const fullPollingActiveRef = useRef(false);
  const lightPollingActiveRef = useRef(false);

  // Clean up polling on unmount: clear any scheduled timeout
  useEffect(() => {
    return () => {
      fullPollingActiveRef.current = false;
      if (fullPollingTimeoutRef.current !== null) {
        clearTimeout(fullPollingTimeoutRef.current);
        fullPollingTimeoutRef.current = null;
      }
      lightPollingActiveRef.current = false;
      if (lightPollingTimeoutRef.current !== null) {
        clearTimeout(lightPollingTimeoutRef.current);
        lightPollingTimeoutRef.current = null;
      }
    };
  }, []);

  // Create polling functions
  const startFullBundlePolling = useCallback(
    (bundleId: string) => {
      if (fullPollingTimeoutRef.current !== null) {
        clearTimeout(fullPollingTimeoutRef.current);
        fullPollingTimeoutRef.current = null;
      }

      fullPollingActiveRef.current = true;

      const poll = () => {
        if (!fullPollingActiveRef.current) return;

        triggerFullBundleDownloadStatus({
          bundleId: bundleId,
          bundleKey: BundleType.FULL,
        })
          .then((response) => {
            if (!fullPollingActiveRef.current) return;

            const currentStatus = response.data?.status;

            if (currentStatus === BundleDownloadStatusType.DOWNLOAD_READY) {
              dispatch(
                setBundleDownloadStatus({
                  key: BundleType.FULL,
                  data: BundleDownloadStatusType.DOWNLOAD_READY,
                }),
              );
              fullPollingActiveRef.current = false;
              fullPollingTimeoutRef.current = null;
              return;
            }

            if (currentStatus === BundleDownloadStatusType.DOWNLOAD_PREPARATION_FAILED) {
              dispatch(
                setBundleDownloadStatus({
                  key: BundleType.FULL,
                  data: BundleDownloadStatusType.DOWNLOAD_PREPARATION_FAILED,
                }),
              );
              setIsGeneratingFull(false);
              setFullBundleRequestError('Bundle Generation failed, please refresh the page and try again');
              globalToastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Bundle Generation failed, please refresh the page and try again',
                life: 5000,
              });
              fullPollingActiveRef.current = false;
              fullPollingTimeoutRef.current = null;
              return;
            }

            // Only continue polling when download is actively being prepared.
            // IN_PROGRESS means nobody requested a download — stop polling.
            if (currentStatus === BundleDownloadStatusType.DOWNLOAD_PREPARING) {
              const timeout = setTimeout(poll, BUNDLE_DOWNLOAD_POLL_INTERVAL_MS);
              fullPollingTimeoutRef.current = timeout;
            } else {
              setIsGeneratingFull(false);
              fullPollingActiveRef.current = false;
              fullPollingTimeoutRef.current = null;
            }
          })
          .catch(() => {
            setIsGeneratingFull(false);
            fullPollingActiveRef.current = false;
            fullPollingTimeoutRef.current = null;
          });
      };

      poll();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [triggerFullBundleDownloadStatus],
  );

  const startLightBundlePolling = useCallback(
    (bundleId: string) => {
      if (lightPollingTimeoutRef.current !== null) {
        clearTimeout(lightPollingTimeoutRef.current);
        lightPollingTimeoutRef.current = null;
      }

      lightPollingActiveRef.current = true;

      const poll = () => {
        if (!lightPollingActiveRef.current) return;

        triggerLightBundleDownloadStatus({
          bundleId: bundleId,
          bundleKey: BundleType.LIGHT,
        })
          .then((response) => {
            if (!lightPollingActiveRef.current) return;

            const currentStatus = response.data?.status;

            if (currentStatus === BundleDownloadStatusType.DOWNLOAD_READY) {
              dispatch(
                setBundleDownloadStatus({
                  key: BundleType.LIGHT,
                  data: BundleDownloadStatusType.DOWNLOAD_READY,
                }),
              );
              lightPollingActiveRef.current = false;
              lightPollingTimeoutRef.current = null;
              return;
            }

            if (currentStatus === BundleDownloadStatusType.DOWNLOAD_PREPARATION_FAILED) {
              dispatch(
                setBundleDownloadStatus({
                  key: BundleType.LIGHT,
                  data: BundleDownloadStatusType.DOWNLOAD_PREPARATION_FAILED,
                }),
              );
              setIsGeneratingLight(false);
              setLightBundleRequestError('Bundle Generation failed, please refresh the page and try again');
              globalToastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Bundle Generation failed, please refresh the page and try again',
                life: 5000,
              });
              lightPollingActiveRef.current = false;
              lightPollingTimeoutRef.current = null;
              return;
            }

            // Only continue polling when download is actively being prepared.
            if (currentStatus === BundleDownloadStatusType.DOWNLOAD_PREPARING) {
              const timeout = setTimeout(poll, BUNDLE_DOWNLOAD_POLL_INTERVAL_MS);
              lightPollingTimeoutRef.current = timeout;
            } else {
              setIsGeneratingLight(false);
              lightPollingActiveRef.current = false;
              lightPollingTimeoutRef.current = null;
            }
          })
          .catch(() => {
            setIsGeneratingLight(false);
            lightPollingActiveRef.current = false;
            lightPollingTimeoutRef.current = null;
          });
      };

      poll();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [triggerLightBundleDownloadStatus],
  );

  const handleFullBundleRequestDownloadManually = useCallback(
    (withPageNumbers: boolean) => {
      // Don't start if already generating
      if (isGeneratingFull) return;

      const bundleId = caseOverview?.fullBundle?.bundleId;
      if (bundleId) {
        setIsGeneratingFull(true);
        setFullBundleRequestError(null);
        triggerBundleRequestDownload({
          bundleId: bundleId,
          withPageNumbers: withPageNumbers,
        })
          .unwrap()
          .then(() => {
            startFullBundlePolling(bundleId);
          })
          .catch((error: { status?: number }) => {
            setIsGeneratingFull(false);
            const message =
              error?.status === 403
                ? "You don't have permission to generate a bundle"
                : 'Bundle Generation failed, please refresh the page and try again';
            setFullBundleRequestError(message);
            globalToastRef.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: message,
              life: 5000,
            });
          });
      }
    },
    [
      caseOverview?.fullBundle?.bundleId,
      isGeneratingFull,
      startFullBundlePolling,
      triggerBundleRequestDownload,
      globalToastRef,
    ],
  );

  const handleLightBundleRequestDownloadManually = useCallback(
    (withPageNumbers: boolean) => {
      // Don't start if already generating
      if (isGeneratingLight) return;

      const bundleId = caseOverview?.lightBundle?.bundleId;
      if (bundleId) {
        setIsGeneratingLight(true);
        setLightBundleRequestError(null);
        triggerBundleRequestDownload({
          bundleId: bundleId,
          withPageNumbers: withPageNumbers,
        })
          .unwrap()
          .then(() => {
            startLightBundlePolling(bundleId);
          })
          .catch((error: { status?: number }) => {
            setIsGeneratingLight(false);
            const message =
              error?.status === 403
                ? "You don't have permission to generate a bundle"
                : 'Bundle Generation failed, please refresh the page and try again';
            setLightBundleRequestError(message);
            globalToastRef.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: message,
              life: 5000,
            });
          });
      }
    },
    [
      caseOverview?.lightBundle?.bundleId,
      isGeneratingLight,
      startLightBundlePolling,
      triggerBundleRequestDownload,
      globalToastRef,
    ],
  );

  const handleDownloadFullBundle = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      // Prevent double-clicking and interference with light bundle
      if (fullBundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_READY && !fullBundleDownloadProgress) {
        const bundleId = caseOverview?.fullBundle?.bundleId;
        if (bundleId) {
          triggerBundleGetDownload({ bundleId })
            .unwrap()
            .then((data) => {
              if (data && data.bundleDownload) {
                downloadFilesAsZip(
                  [data.bundleDownload],
                  `${caseId}_full_bundle_${format(new Date(), 'y_MMM_d_HH_mm')}`,
                  (progress) => {
                    setFullBundleDownloadProgress(progress);
                    if (progress === 100) {
                      setTimeout(() => {
                        setFullBundleDownloadProgress(null);
                        // Reset both local state and Redux status to allow new generation
                        setIsGeneratingFull(false);
                        dispatch(
                          setBundleDownloadStatus({
                            key: BundleType.FULL,
                            data: BundleDownloadStatusType.IN_PROGRESS,
                          }),
                        );
                      }, 500);
                    }
                  },
                );
              }
            });
        }
      }
    },
    [
      caseId,
      caseOverview?.fullBundle?.bundleId,
      fullBundleDownloadStatus,
      fullBundleDownloadProgress,
      triggerBundleGetDownload,
      dispatch,
    ],
  );

  const handleDownloadLightBundle = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      // Prevent double-clicking and interference with full bundle
      if (lightBundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_READY && !lightBundleDownloadProgress) {
        const bundleId = caseOverview?.lightBundle?.bundleId;
        if (bundleId) {
          triggerBundleGetDownload({ bundleId })
            .unwrap()
            .then((data) => {
              if (data && data.bundleDownload) {
                downloadFilesAsZip(
                  [data.bundleDownload],
                  `${caseId}_light_bundle_${format(new Date(), 'y_MMM_d_HH_mm')}`,
                  (progress) => {
                    setLightBundleDownloadProgress(progress);
                    if (progress === 100) {
                      setTimeout(() => {
                        setLightBundleDownloadProgress(null);
                        // Reset both local state and Redux status to allow new generation
                        setIsGeneratingLight(false);
                        dispatch(
                          setBundleDownloadStatus({
                            key: BundleType.LIGHT,
                            data: BundleDownloadStatusType.IN_PROGRESS,
                          }),
                        );
                      }, 500);
                    }
                  },
                );
              }
            });
        }
      }
    },
    [
      caseId,
      caseOverview?.lightBundle?.bundleId,
      lightBundleDownloadStatus,
      lightBundleDownloadProgress,
      triggerBundleGetDownload,
      dispatch,
    ],
  );

  const handleDownloadTimeline = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (caseId) {
        triggerTimelineGetDownload({ caseId: caseId })
          .unwrap()
          .then((data) => {
            if (data && data.downloadPath && data.downloadToken) {
              downloadFile(
                `${caseId}_timeline_${format(new Date(), 'y_MMM_d_HH_mm')}`,
                data.downloadPath,
                data.downloadToken,
              );
            }
          });
      }
    },
    [caseId, downloadFile, triggerTimelineGetDownload],
  );

  // `templateId` (optional) applies a custom CHRONOLOGY template; omitted for
  // the default template.
  const handleDownloadChronology = useCallback(
    (returnFullBundlePageNumbers: boolean, templateId?: string) => {
      if (caseId) {
        triggerChronologyGetDownload({
          caseId,
          return_full_bundle_page_numbers: returnFullBundlePageNumbers,
          template_id: templateId,
        })
          .unwrap()
          .then((data) => {
            if (data && data.downloadPath && data.downloadToken) {
              downloadFile(
                `${caseId}_chronology_${format(new Date(), 'y_MMM_d_HH_mm')}`,
                data.downloadPath,
                data.downloadToken,
              );
            }
          });
      }
    },
    [caseId, downloadFile, triggerChronologyGetDownload],
  );

  // `templateId` (optional) applies a custom INDEX template; omitted for the
  // default index template.
  const handleDownloadFullBundleIndex = useCallback(
    (templateId?: string) => {
      const bundleId = caseOverview?.fullBundle?.bundleId;
      if (bundleId) {
        setFullIndexDownloadError(null);
        triggerBundleGetDownloadIndex({ bundleId, templateId })
          .unwrap()
          .then((data) => {
            if (data && data.downloadPath && data.downloadToken) {
              downloadFile(
                `${caseId}_full_bundle_index_${format(new Date(), 'y_MMM_d_HH_mm')}`,
                data.downloadPath,
                data.downloadToken,
              );
            }
          })
          .catch((error: { status?: number }) => {
            const message =
              error?.status === 403
                ? "You don't have permission to download the index"
                : 'Failed to download DOCX index. Please refresh the page and try again';
            setFullIndexDownloadError(message);
            globalToastRef.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: message,
              life: 5000,
            });
          });
      }
    },
    [caseId, caseOverview?.fullBundle?.bundleId, downloadFile, triggerBundleGetDownloadIndex, globalToastRef],
  );

  const handleDownloadLightBundleIndex = useCallback(
    (templateId?: string) => {
      const bundleId = caseOverview?.lightBundle?.bundleId;
      if (bundleId) {
        setLightIndexDownloadError(null);
        triggerBundleGetDownloadIndex({ bundleId, templateId })
          .unwrap()
          .then((data) => {
            if (data && data.downloadPath && data.downloadToken) {
              downloadFile(
                `${caseId}_light_bundle_index_${format(new Date(), 'y_MMM_d_HH_mm')}`,
                data.downloadPath,
                data.downloadToken,
              );
            }
          })
          .catch((error: { status?: number }) => {
            const message =
              error?.status === 403
                ? "You don't have permission to download the index"
                : 'Failed to download DOCX index. Please refresh the page and try again';
            setLightIndexDownloadError(message);
            globalToastRef.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: message,
              life: 5000,
            });
          });
      }
    },
    [caseId, caseOverview?.lightBundle?.bundleId, downloadFile, triggerBundleGetDownloadIndex, globalToastRef],
  );
  return {
    fullBundleDownloadStatus,
    lightBundleDownloadStatus,
    fullBundleDownloadProgress,
    lightBundleDownloadProgress,
    isGeneratingFull,
    isGeneratingLight,
    fullBundleRequestError,
    lightBundleRequestError,
    fullIndexDownloadError,
    lightIndexDownloadError,
    handleDownloadFullBundle,
    handleDownloadLightBundle,
    handleDownloadFullBundleIndex,
    handleDownloadLightBundleIndex,
    handleDownloadTimeline,
    handleDownloadChronology,
    handleFullBundleRequestDownloadManually,
    handleLightBundleRequestDownloadManually,
    chronologyTemplates,
    indexTemplates,
  };
};
